<template>
  <div ref="tile" class="information-item generic-border generic-border--clickable" :class="mainElClasses">
    <RouterLink :to="getInfoCreationLink(info.id)" :event="clickDisabled ? '' : 'click'">
      <div class="information-item__wrapper v-centerer">

        <div v-if="isGw" class="information-item__icon">
          <Icon>{{ hardhatExtraBold }}</Icon>
        </div>
        <div v-else-if="isSuggestion" class="information-item__icon">
          <Icon>{{ bulbMedium }}</Icon>
        </div>

        <div class="information-item__info">
          <EditableLabel
            :value="relatedGw ? gwMessageIntro : info.text"
            class="information-item__info__text"
            disabled
            ellipsize
            use-tiptap-editor
            stop-propagation
            :display-tiptap-border="false"
            :placeholder="$t('message.INFO_MESSAGE')"
          />
        </div>

        <div class="information-item__author h-v-centerer">
          <UserThumbnail
            v-if="authorUser"
            :value="authorUser.id"
            disabled
            fetch-data
            row
            show-name
            stop-propagation
          />
          <div v-else>{{ info.senderName }}</div>
        </div>

        <div class="information-item__release h-v-centerer">
          {{ dispDate }}
        </div>

        <div class="information-item__removal h-v-centerer">
          {{ dispEndDate }}
        </div>

        <div class="information-item__files h-v-centerer" :class="{ 'greyed-out': clickDisabled }">
          <Badge :color="info.files && info.files.length > 0 ? SColors.mainTextColor : SColors.mediumGray">
            <span slot="badge">{{ info.files ? info.files.length : 0 }}</span>
            <div
              class="generic-border generic-border--icon-button generic-border--clickable generic-border--blue-border"
              @click.prevent="clickDisabled ? '' : $refs.chipList.onChipClick()"
            >
              <Icon>{{ galleryMedium }}</Icon>
            </div>
          </Badge>
        </div>

        <div
          v-if="!noButtons"
          class="information-item__files h-v-centerer"
          :class="{ 'greyed-out': !allowForward }"
        >
          <div
            class="generic-border generic-border--icon-button generic-border--clickable generic-border--blue-border"
            @click.prevent="onClickForward"
          >
            <Icon>{{ forward }}</Icon>
          </div>
        </div>

        <div v-if="!noButtons" class="information-item__files h-v-centerer" :class="{ 'greyed-out': editionDisabled }">
          <div
            class="generic-border generic-border--icon-button generic-border--clickable generic-border--blue-border"
            @click.prevent="promptDeleteDialog"
          >
            <Icon>{{ deleteMedium }}</Icon>
          </div>
        </div>
      </div>

    </RouterLink>

    <FileChipList
      ref="chipList"
      v-model="info.files"
      class="information-item__hidden-chips"
      :editable="!editionDisabled"
      pathname="informations/"
      own-file-viewer
      @openFileViewer="$emit('openFileViewer', $event)"
      @input="editInfo()"
    />

    <AdvancedTeamPicker
      v-if="forwardingInfo"
      v-model="forwardTeams"
      class="italic info-teams"
      legend=""
      ok-button
      invisible
      edit-mode-always-on
      display-all-teams
      :disable-inaccessible="currentTeam.allowSendingInfoAnyTeam !== 1"
      @input="forwardInfo"
      @endEdit="forwardingInfo = false"
    />

  </div>
</template>

<script>
/**
 * Component to display informations on related pages
 *
 * @vue-prop {Object} value - info object from the database
 * @vue-prop {Boolean} disabled - info shouldn't be editable
 * @vue-prop {Boolean} isGw - info is about an upcoming field tour
 * @vue-prop {Boolean} isSuggestion - info is a meeting suggestion
 * @vue-prop {Boolean} noButtons - Hide the buttons
 * @vue-prop {Boolean} noHeightLimit - By default, information items are limited to 3 times a usual line height.
 *                                     Setting noHiehgtLimit to true override this behavior.
 *
 */
import $ from 'jquery';
import toastr from 'toastr';
import cloneDeep from 'lodash/cloneDeep';
// Components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import Badge from '@/components/badge.vue';
import EditableLabel from '@/components/editable-label.vue';
import FileChipList from '@/components/file-system/file-chip-list.vue';
import Icon from '@/components/icon.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
// Socket utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Vuex utils
import { FETCH_INFOS, EDIT_INFO, DELETE_INFO } from '@/store/action-types';
import { FETCH_GW } from '@/store/modules/gemba-walks/action-types';
// Utils
import { calert, decodeHTML, itemWidthClasses } from '@/utils/main';
import { SColors } from '@/utils/constants';
import { getGWInfoSummary } from '@/utils/gw-utils';
// Assets
import {
  bulbMedium,
  deleteMedium,
  forward,
  galleryMedium,
  hardhatExtraBold,
} from '@/assets/icons';

export default {

  name: 'InformationItem',

  components: {
    AdvancedTeamPicker,
    Badge,
    EditableLabel,
    FileChipList,
    Icon,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler],

  props: {
    value: {
      type: Object,
      required: true,
    },
    disabled: Boolean,
    isGw: Boolean,
    isSuggestion: Boolean,
    noButtons: Boolean,
    noHeightLimit: Boolean,
  },

  data() {
    return {
      bulbMedium,
      deleteMedium,
      forward,
      galleryMedium,
      hardhatExtraBold,
      SColors,
      elClasses: '',
      forwardingInfo: false,
      forwardTeams: [],
      info: cloneDeep(this.value),
    };
  },

  computed: {
    /** Object: CSS classes applied to this.$el */
    mainElClasses() {
      return {
        ...this.elClasses,
        'information-item--no-height-limit': this.noHeightLimit,
      };
    },
    /** Boolean: True if the item shouldn't be editable by the current user */
    editionDisabled() {
      return this.disabled || !this.isCurrentTeam || this.isGw || this.isSuggestion;
    },
    /** Boolean: True if the item shouldn't be clickable by the current user */
    clickDisabled() {
      return this.isGw || this.isSuggestion;
    },
    /** Boolean: May this information be forwarded */
    allowForward() {
      const managedTeams = this.$store.getters.teamsTreeCurrentFlattened;
      return !this.disabled && !this.isGw && !this.isSuggestion && managedTeams.length > 0;
    },
    /** String: Date format according to the available space */
    dateFormat() {
      return this.min || this.$store.state.device.isSmartphonePortrait
        ? this.$t('minDateFormat')
        : this.$t('longFullDateFormat');
    },
    /** String: Displayed publish date of the information */
    dispDate() {
      return this.$moment(this.info.published).format(this.dateFormat);
    },
    /** String: Displayed unpublish date of the information */
    dispEndDate() {
      return this.$moment(this.info.unpublished).format(this.dateFormat);
    },
    /** Boolean: Whether the team that sent this info is the current one */
    isCurrentTeam() {
      return Number(this.info.publisher) === this.currentTeam.id;
    },
    /** Object: User object corresponding to info.author. Null if not found */
    authorUser() {
      return this.info.author > 0 && this.$store.getters.companyUsers[`${this.info.author}`]
        ? this.$store.getters.companyUsers[`${this.info.author}`] || null
        : null;
    },
    /** String: Text displayed in the info's head about who created this info */
    dispFromTeam() {
      return this.isCurrentTeam
        ? this.$t('message.FROM_YOUR_TEAM')
        : this.$t('message.FROM_TEAM', { team: decodeHTML(this.info.senderName) });
    },
    /** gwIntro */
    gwMessageIntro() {
      return getGWInfoSummary(this.relatedGw, this.currentTeam, this.info.text);
    },
    relatedGw() {
      return this.$store.state.gembaWalks.gwPerId[`${this.info.gwToRate || 0}`];
    },
  },

  watch: {
    value(val) {
      this.info = cloneDeep(val);
      this.forwardTeams = cloneDeep(this.info.teams || []);
      this.checkRelatedGw();
    },
    forwardingInfo() {
      this.$emit('openTeamSelection', this.forwardingInfo);
    },
  },

  created() {
    this.info = cloneDeep(this.value);
    this.forwardTeams = cloneDeep(this.info.teams || []);
  },

  mounted() {
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
    this.checkRelatedGw();
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
  },

  methods: {
    checkRelatedGw() {
      if (this.info.gwToRate) {
        this.$store.dispatch(`gembaWalks/${FETCH_GW}`, { id: this.info.gwToRate });
      }
    },
    /**
     * Will refresh info cache in Vuex
     */
    fetchInfos() {
      return this.$store.dispatch(FETCH_INFOS);
    },
    /**
     * Forward an information from current team to other teams
     */
    forwardInfo() {
      const teams = this.forwardTeams;
      if (teams.length === 0 || (teams.length === 1 && teams[0] === this.currentTeam.id)) {
        calert(this.$t('message.INFO_FW_PICK_TEAM'), [{ title: this.$t('message.CANCEL') }]);
        return;
      }
      this.forwardingInfo = false;
      // Post request
      const params = cloneDeep(this.info);
      params.start = this.info.published;
      params.end = this.info.unpublished;
      params.message = this.info.text;
      params.teams = this.forwardTeams;
      this.$store.dispatch(EDIT_INFO, { ...this.info, teams: this.forwardTeams }).then((data) => {
        if (data) {
          toastr.success(this.$t('message.INFO_SAVED'));
          this.fetchInfos();
          this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.CC_MSGS.MEETING_INFOS });
          this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.NOTIF_MSG });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    deleteInfo() {
      if (this.editionDisabled) {
        return;
      }
      this.$store.dispatch(DELETE_INFO, { id: this.info.id }).then((data) => {
        if (data) {
          toastr.success(this.$t('message.INFO_DELETED'));
          this.fetchInfos();
          this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.CC_MSGS.MEETING_INFOS });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    editInfo() {
      if (this.editionDisabled) {
        return;
      }
      const info = cloneDeep(this.info);
      delete info.start;
      delete info.end;
      this.$store.dispatch(EDIT_INFO, { ...info }).then((data) => {
        if (data) {
          toastr.success(this.$t('message.INFO_SAVED'));
          this.fetchInfos();
          this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.CC_MSGS.MEETING_INFOS });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    promptDeleteDialog() {
      if (this.editionDisabled) return;
      calert(this.$t('message.INFO_DELETE_CONFIRM'), [
        {
          title: this.$t('message.INFO_DELETE'),
          action: this.deleteInfo,
          sClass: 'red',
        }, {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /**
     * Teams which manage other teams may forward information to them
     */
    onClickForward() {
      if (this.allowForward) {
        this.forwardingInfo = true;
      }
    },
    /** Prompts the user to rate and review the GW */
    promptRateGW() {
      calert(this.$t('message.GW_PROMPT_RATE_REVIEW'), [
        {
          title: this.$t('message.YES'),
          action: () => {
            this.$router.push({ path: '/gw-rating', query: { gwId: this.relatedGw.id } });
          },
        }, {
          title: this.$t('message.NO'),
        },
      ]);
    },
    /** Style of the problem is determined by the size of $el. Apply classes */
    onSizeChanged() {
      const width = this.$refs.tile ? this.$refs.tile.offsetWidth : 0;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.information-item {
  margin-top: 0.8rem;
  font-size: 0.86rem;

  &--no-height-limit {
    .information-item {
      &__info {
        max-height: initial;

        .editable-label__text-field {
          max-height: initial;
        }

        &:after {
          display: none;
        }
      }
    }
  }

  &__hidden-chips {
    width: 0px;
    height: 0px;
  }

  &__files {

    .badge__badge {
      top: -8px;
      right: -8px;
    }
  }

  &__info {
    flex: 1;
    min-width: 0px;
    font-size: 1rem;
    line-height: 1.3rem;
    display: flex;
    align-items: center;
    max-height: 3.9rem;
    font-weight: 600;
    margin-left: 1rem;
    padding: 0.7rem 0;
    overflow: hidden;
    position: relative;

    .editable-label__text-field {
      margin: 0;
      max-height: 3.9rem;
      overflow: hidden;

      &.text-field .ProseMirror, &.text-field .text-field__field__content {
        padding: 0;
      }
    }

    // Display a white inset shadow to fade the content of the info instead of cutting it
    &:after {
      content: " ";
      position: absolute;
      bottom: .7rem;
      left: 0;
      right: 0;
      height: 2.5rem;
      box-shadow: inset 0 -5px 10px white;
    }
  }

  &__icon {
    margin-left: 1rem;

    .icon {
      width: 1rem;
      height: 1rem;
    }
  }

  &__author {

    .user-thumbnail__avatar {
      width: 1.5rem;
      height: 1.5rem;
      font-size: .8rem;
      flex: 0 0 1.5rem;
    }
  }

  &__release, &__removal, &__author {
    text-align: center;
    flex: 0 0 11rem;
  }

  &__files, &__forward, &__delete {
    flex: 0 0 5.72rem;
    margin: .64rem 0;
  }

  &.tablet-size {
    .information-item {
      &__release, &__removal, &__author {
        flex: 0 0 8.5rem;
      }

      &__files, &__forward, &__delete {
        flex: 0 0 4.72rem;
      }
    }
  }

  &.sm-landscape-size {
    .information-item {
      &__release, &__removal, &__author {
        flex: 0 0 6rem;
      }

      &__files, &__forward, &__delete {
        flex: 0 0 3.72rem;
      }
    }
  }

  &.sm-portrait-size {
    .information-item {
      &__release, &__removal, &__author {
        display: none;
      }
    }
  }
}
</style>
